<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, RotateCcw, Search } from '@lucide/vue'
import {
  Badge, Button, ConfirmAction, EmptyState, Input, Pagination, Section, Select,
  Spinner, Stat, Table, Toolbar, useToast,
} from '@drama/ui'

import { disableIntentVideo, enableIntentVideo, listIntentVideos } from '../../api/intent-videos.js'
import { formatDate, formatMs, pageSummary, statusLabel, statusVariant } from './format.js'

const props = defineProps({ scenarioId: { type: String, required: true } })

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const togglingId = ref('')
const loading = ref(false)
const error = ref('')
const items = ref([])
const counts = ref({ total_resources: 0, available: 0, disabled: 0, with_video: 0, prompt_only: 0 })
const coverage = ref(null)
const filters = reactive({ status: '', has_video: '', q: '', sort: 'created_at', order: 'desc' })
const pageSize = ref(50)
const cursors = ref([])
const nextCursor = ref(null)

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allStatuses') },
  ...['in_review', 'published', 'rejected'].map(status => ({ value: status, label: statusLabel(t, status) })),
])

const hasVideoOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.intentVideo.filters.allVideo') },
  { value: 'yes', label: t('visualGameAdmin.intentVideo.filters.withVideo') },
  { value: 'no', label: t('visualGameAdmin.intentVideo.filters.promptOnly') },
])

const sortOptions = computed(() => [
  { value: 'created_at', label: t('visualGameAdmin.intentVideo.table.createdAt') },
  { value: 'status', label: t('visualGameAdmin.intentVideo.table.status') },
  { value: 'intent', label: t('visualGameAdmin.intentVideo.table.intent') },
])

const orderOptions = computed(() => [
  { value: 'desc', label: t('visualGameAdmin.intentVideo.filters.desc') },
  { value: 'asc', label: t('visualGameAdmin.intentVideo.filters.asc') },
])

const columns = computed(() => [
  { key: 'status', label: t('visualGameAdmin.intentVideo.table.status'), width: '100px' },
  { key: 'intent', label: t('visualGameAdmin.intentVideo.table.intent'), width: '320px' },
  { key: 'video', label: t('visualGameAdmin.intentVideo.table.video'), width: '64px' },
  { key: 'prompt', label: 'Prompt', width: '72px' },
  { key: 'duration', label: t('visualGameAdmin.intentVideo.table.duration'), width: '90px', align: 'right', mono: true },
  { key: 'resolution', label: t('visualGameAdmin.intentVideo.table.resolution'), width: '110px', mono: true },
  { key: 'hit_count', label: t('visualGameAdmin.intentVideo.table.hits'), width: '80px', align: 'right', mono: true },
  { key: 'created_at', label: t('visualGameAdmin.intentVideo.table.createdAt'), width: '170px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '150px', align: 'right', fixed: 'right' },
])

const coverageRate = computed(() => {
  if (coverage.value === null) return '-'
  return `${(coverage.value.coverage_rate * 100).toFixed(1)}%`
})

const summary = computed(() => pageSummary(t, { loaded: items.value.length, hasNext: nextCursor.value !== null }))

onMounted(() => load(null))

async function load(cursor) {
  loading.value = true
  error.value = ''
  try {
    const data = await listIntentVideos({
      scenario_id: props.scenarioId,
      limit: pageSize.value,
      cursor: cursor ?? undefined,
      status: filters.status || undefined,
      has_video: filters.has_video || undefined,
      q: filters.q.trim() || undefined,
      sort: filters.sort,
      order: filters.order,
    })
    items.value = data.items
    counts.value = data
    coverage.value = data.coverage
    nextCursor.value = data.next_cursor
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  cursors.value = []
  load(null)
}

function resetFilters() {
  filters.status = ''
  filters.has_video = ''
  filters.q = ''
  filters.sort = 'created_at'
  filters.order = 'desc'
  applyFilters()
}

function nextPage() {
  cursors.value.push(nextCursor.value)
  load(nextCursor.value)
}

function prevPage() {
  cursors.value.pop()
  load(cursors.value[cursors.value.length - 1] ?? null)
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

function openDetail(row) {
  router.push({ name: 'intent-video-resource-detail', params: { resourceId: row.id } })
}

/** 恢复不需要确认：它只是把资源放回召回池，做错了再禁用一次就行。 */
async function enable(row) {
  togglingId.value = row.id
  try {
    await enableIntentVideo(row.id)
    toast.success(t('visualGameAdmin.intentVideo.detail.enabled'))
    await reload()
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    togglingId.value = ''
  }
}

/** 禁用走 ConfirmAction：它自己弹确认、自己调 action、完了 emit done。 */
function disableAction(row) {
  return () => disableIntentVideo(row.id)
}

async function onDisabled() {
  toast.success(t('visualGameAdmin.intentVideo.detail.disabled'))
  await reload()
}

function reload() {
  return load(cursors.value[cursors.value.length - 1] ?? null)
}
</script>

<template>
  <div class="ivr vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/ResourcesTab.vue#resources">
    <Section>
      <div class="ivr__stats">
        <Stat :label="t('visualGameAdmin.intentVideo.stats.total')" :value="String(counts.total_resources)" />
        <Stat :label="t('visualGameAdmin.intentVideo.stats.withVideo')" :value="String(counts.with_video)" />
        <Stat :label="t('visualGameAdmin.intentVideo.stats.promptOnly')" :value="String(counts.prompt_only)" />
        <Stat :label="t('visualGameAdmin.intentVideo.stats.disabled')" :value="String(counts.disabled)" />
        <Stat :label="t('visualGameAdmin.intentVideo.stats.coverage')" :value="coverageRate" />
      </div>
      <p v-if="coverage" class="ivr__coverage-note">
        {{ t('visualGameAdmin.intentVideo.stats.coverageNote', {
          covered: coverage.covered_turns,
          total: coverage.total_turns,
          inputs: coverage.covered_inputs,
          uniqueInputs: coverage.unique_inputs,
        }) }}
      </p>
    </Section>


    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.intentVideo.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.intentVideo.filters.resourceSearchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.table.video') }}</label>
          <Select v-model="filters.has_video" :options="hasVideoOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.filters.sort') }}</label>
          <Select v-model="filters.sort" :options="sortOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.intentVideo.filters.order') }}</label>
          <Select v-model="filters.order" :options="orderOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.intentVideo.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load(null)">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState
          v-if="items.length === 0"
          :title="t('visualGameAdmin.intentVideo.resources.emptyTitle')"
          :description="t('visualGameAdmin.intentVideo.resources.emptyDescription')"
        />
        <Table v-else :columns="columns" :rows="items" clickable @row-click="openDetail">
          <template #status="{ row }">
            <Badge :variant="statusVariant(row.status)" size="sm">{{ statusLabel(t, row.status) }}</Badge>
          </template>
          <template #intent="{ row }">
            <span class="ivr__clamp" :title="row.intent">{{ row.intent }}</span>
          </template>
          <template #video="{ row }">
            <span class="ivr__dot" :class="row.video_url ? 'is-on' : 'is-off'" />
          </template>
          <template #prompt="{ row }">
            <span class="ivr__dot" :class="row.h3_prompt ? 'is-on' : 'is-off'" />
          </template>
          <template #duration="{ row }">{{ formatMs(row.video_duration_ms) }}</template>
          <template #resolution="{ row }">
            <template v-if="row.video_width">{{ row.video_width }}×{{ row.video_height }}</template>
            <template v-else>-</template>
          </template>
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #actions="{ row }">
            <div class="ivr__actions">
              <Button variant="secondary" size="sm" @click.stop="openDetail(row)">
                <Eye :size="14" /> {{ t('visualGameAdmin.common.view') }}
              </Button>
              <Button
                v-if="row.status === 'rejected'"
                variant="ghost"
                size="sm"
                :loading="togglingId === row.id"
                @click.stop="enable(row)"
              >
                <RotateCcw :size="14" /> {{ t('visualGameAdmin.intentVideo.detail.enable') }}
              </Button>
              <!-- ConfirmAction 自带触发按钮、没有默认插槽，所以文案走 label 而不是塞子元素。
                   外面这层 stop 是拦行点击：确认按钮的 click 会冒泡到 tr 上去开详情。 -->
              <span v-else @click.stop>
                <ConfirmAction
                  :label="t('visualGameAdmin.intentVideo.detail.disable')"
                  :confirm-text="t('visualGameAdmin.intentVideo.detail.confirmDisable')"
                  :cancel-label="t('visualGameAdmin.common.cancel')"
                  :ok-label="t('visualGameAdmin.common.confirm')"
                  :pending-label="t('visualGameAdmin.common.loading')"
                  danger
                  :action="disableAction(row)"
                  @done="onDisabled"
                  @error="requestError => toast.error(requestError.message, { duration: 0 })"
                />
              </span>
            </div>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="cursors.length > 0"
        :has-next="nextCursor !== null"
        :loading="loading"
        :summary="summary"
        :prev-label="t('visualGameAdmin.intentVideo.pager.prev')"
        :next-label="t('visualGameAdmin.intentVideo.pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="t('visualGameAdmin.intentVideo.pager.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>
  </div>
</template>

<style scoped>
.ivr__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: var(--drama-space-md); }
.ivr__coverage-note { margin: var(--drama-space-sm) 0 0; font-size: 12px; color: var(--drama-color-text-muted); }
.ivr__clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.5; }
.ivr__dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
.ivr__dot.is-on { background: var(--drama-color-success); }
.ivr__dot.is-off { background: var(--drama-color-border); }
.ivr__actions { display: flex; gap: var(--drama-space-xs); justify-content: flex-end; }






</style>
